import { useAppSelector } from "@/hooks/redux";
import {
  selectCameraApiCredentials,
  selectCameraApiStatus,
  selectCameraApiUrl,
  type CameraApiStatus,
} from "@/modules/project/services/projectSlice";

/**
 * Base URL for the active project's camera.
 *
 * Delegates to {@link selectCameraApiUrl} rather than reading
 * `project.cameraApiUrl` directly, so the hook and the RTK Query base query can
 * never disagree about where the camera is. They did once: the selector had
 * been taught about the relay and this hook had not, so every RTK Query request
 * went to the relay correctly while WebRTC, the report `<img>` URLs and the
 * dashboard iframe all silently got null.
 */
export const useCameraApiUrl = (): string | null =>
  useAppSelector(selectCameraApiUrl);

/**
 * Why the camera is or isn't reachable — for pages that have to render the
 * difference between "connecting", "the relay is down", "this organization may
 * not use a local address" and "nothing is configured".
 */
export const useCameraApiStatus = (): CameraApiStatus =>
  useAppSelector(selectCameraApiStatus);

/**
 * Credentials mode for a raw `fetch` against {@link useCameraApiUrl}'s base.
 *
 * Needed because a hardcoded `credentials: "include"` breaks direct LAN access:
 * the device's wildcard CORS response is refused for credentialed requests, so
 * the browser blocks the request before it leaves. RTK Query gets the same value
 * through the camera base query; raw fetches have to ask for it.
 */
export const useCameraApiCredentials = (): RequestCredentials =>
  useAppSelector(selectCameraApiCredentials);
